import { OUTPUT_HEIGHT, OUTPUT_WIDTH } from 'modules/match-video/domain/assembler/constants';
import { ReviewCanvasContext, ReviewScene } from 'modules/match-video/domain/interfaces';
import {
  baselineFromTop,
  Box,
  browserSceneEnv,
  cssLinearGradient,
  drawContained,
  fillBlock,
  fontMetrics,
  measureBlock,
  releaseSceneImage,
  scaleAround,
  SceneCanvas,
  SceneEnv,
  skewX,
  tracePolygon,
} from 'modules/match-video/domain/scenes/kit';
import { IntroSceneData } from 'modules/match-video/domain/scenes/scene-data';
import { SLASH_INTRO_SEC, slashIntroState } from './intro-state';
import { BLEED, SLASH, SLASH_FONTS, SLASH_INTRO_FONTS } from './style';

const CENTER_X = OUTPUT_WIDTH / 2;
const CENTER_Y = OUTPUT_HEIGHT / 2;

/** `.b-half1` / `.b-half2`: both cover the frame plus the bleed and are cut along the seam (1200,−40) → (720,1120). */
const HALF_BOX: Box = { left: -BLEED, top: -BLEED, width: OUTPUT_WIDTH + 2 * BLEED, height: OUTPUT_HEIGHT + 2 * BLEED };
const RED_HALF = [
  [0, 0],
  [0.62, 0],
  [0.38, 1],
  [0, 1],
] as const;
const DARK_HALF = [
  [0.62, 0],
  [1, 0],
  [1, 1],
  [0.38, 1],
] as const;
/** `.b-slash`: a 32 px white bar skewed −22.48° along the seam, grown down from its top (origin 16px 0). */
const SLASH_BAR = { originX: 1192.3 + 16, top: -60, width: 32, height: 1200, skew: -22.48 };
/** `.b-top`: a white band across the top, its bottom edge slanting from 77.5 % on the left to 100 % on the right. */
const TOP_BOX: Box = { left: -BLEED, top: -BLEED, width: OUTPUT_WIDTH + 2 * BLEED, height: 160 };
const TOP_BAND = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 0.775],
] as const;

// Mockup sizes. Minimum sizes come from real data (Eliga, phase 6 journal): the longest names shrink to them, then «…».
const CHAMP = { left: 96, width: OUTPUT_WIDTH - 96 - 420, top: 24, size: 34, min: 26, lineHeight: 1.2 };
const ROUND = { right: OUTPUT_WIDTH - 96, top: 20, size: 38, min: 24, maxWidth: 300 };
const TEAM = { top: 310, width: 640, inset: 150, logo: 220, logoPad: 18, logoRadius: 20, size: 60, min: 44, lineHeight: 1.05 };
/**
 * The logo is an inline-block on a line of the body's 15px/1.5 Inter: under its bottom sits the strut's descent plus
 * half-leading, ≈ 6 px, before the name's `margin-top: 28px`.
 */
const LOGO_LINE_GAP = 6;
const NAME_TOP = TEAM.top + TEAM.logo + LOGO_LINE_GAP + 28;
const LOGO_CENTERS = { home: TEAM.inset + TEAM.logo / 2, away: OUTPUT_WIDTH - TEAM.inset - TEAM.logo / 2 };
const LOGO_CENTER_Y = TEAM.top + TEAM.logo / 2;
/** `.b-score`: white box `skewX(-12deg)`, padding 20 / 50, number cells `min-width: 1.4em`, line-height 1. */
const SCORE = { top: 420, size: 150, padX: 50, padY: 20, cellEm: 1.4, skew: -12 };
const FOOT = { left: 96, bottom: 70, size: 34, min: 24, maxWidth: 1200, spacing: 3 };

/** Room around the logo tile for nothing but antialiasing — the tile has no shadow. */
const LOGO_LAYER_SIZE = TEAM.logo + 4;

/**
 * `.b-logo` drawn once into its own layer: a white rounded tile with the logo fitted inside its 18 px padding. Faded
 * and scaled as a whole, like CSS on the `<img>` element.
 */
function renderLogoLayer(env: SceneEnv, image: HTMLImageElement | null): SceneCanvas {
  const layer = env.createCanvas(LOGO_LAYER_SIZE, LOGO_LAYER_SIZE);
  const { ctx } = layer;
  const offset = (LOGO_LAYER_SIZE - TEAM.logo) / 2;
  ctx.fillStyle = SLASH.white;
  ctx.beginPath();
  ctx.roundRect(offset, offset, TEAM.logo, TEAM.logo, TEAM.logoRadius);
  ctx.fill();
  if (image) {
    const inner = TEAM.logo - 2 * TEAM.logoPad;
    drawContained(ctx, image, offset + TEAM.logoPad, offset + TEAM.logoPad, inner, inner);
  }
  return layer;
}

function drawLogoLayer(ctx: ReviewCanvasContext, layer: SceneCanvas, cx: number, state: { scale: number; alpha: number }): void {
  if (state.alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = state.alpha;
  scaleAround(ctx, cx, LOGO_CENTER_Y, state.scale);
  ctx.drawImage(layer.canvas, cx - LOGO_LAYER_SIZE / 2, LOGO_CENTER_Y - LOGO_LAYER_SIZE / 2);
  ctx.restore();
}

function drawHalf(ctx: ReviewCanvasContext, dx: number, points: typeof RED_HALF | typeof DARK_HALF, from: string, to: string): void {
  ctx.save();
  ctx.translate(dx, 0);
  tracePolygon(ctx, HALF_BOX, points);
  ctx.fillStyle = cssLinearGradient(ctx, HALF_BOX, 135, [
    [0, from],
    [1, to],
  ]);
  ctx.fill();
  ctx.restore();
}

/**
 * Set B intro «Спортивный скос», 4 s: the red and the dark halves slide together, a white slash grows along their
 * seam, a white band drops from the top with the championship and the stage, the logos slam in and the frame
 * shakes, the names slide in from outside, the score box unfolds with an overshoot and counts up; the frame pushes in.
 *
 * Built once (fonts, logos, fitted texts) — `draw` only paints.
 */
export async function createSportSlashIntro(data: IntroSceneData, env: SceneEnv = browserSceneEnv): Promise<ReviewScene> {
  const [, tabular, homeImage, awayImage] = await Promise.all([
    env.loadFonts(SLASH_INTRO_FONTS),
    env.loadTabularFont('Russo One', 400),
    env.loadImage(data.home.logoUrl),
    env.loadImage(data.away.logoUrl),
  ]);
  const homeLogo = renderLogoLayer(env, homeImage);
  const awayLogo = renderLogoLayer(env, awayImage);
  releaseSceneImage(homeImage);
  releaseSceneImage(awayImage);
  const scoreFont = tabular ? `400 ${SCORE.size}px "${tabular}"` : SLASH_FONTS.score(SCORE.size);
  const measurer = env.createCanvas(1, 1);
  const m = measurer.ctx;

  const champ = measureBlock(m, data.championshipName.toLocaleUpperCase(), {
    maxWidth: CHAMP.width,
    size: CHAMP.size,
    min: CHAMP.min,
    font: SLASH_FONTS.champ,
    lineHeight: CHAMP.lineHeight,
  });
  const round = data.roundName
    ? measureBlock(m, data.roundName.toLocaleUpperCase(), {
        maxWidth: ROUND.maxWidth,
        size: ROUND.size,
        min: ROUND.min,
        font: SLASH_FONTS.round,
      })
    : null;
  const teamName = (name: string) =>
    measureBlock(m, name.toLocaleUpperCase(), {
      maxWidth: TEAM.width,
      size: TEAM.size,
      min: TEAM.min,
      font: SLASH_FONTS.teamName,
      maxLines: 2,
      lineHeight: TEAM.lineHeight,
    });
  const homeName = teamName(data.home.name);
  const awayName = teamName(data.away.name);

  // Equal number cells, as wide as the final scores need (at least 1.4 em): the colon stays on the box centre and the
  // box keeps its width while the score counts up.
  m.font = scoreFont;
  const homeFinal = data.homeScore === undefined ? '–' : String(data.homeScore);
  const awayFinal = data.awayScore === undefined ? '–' : String(data.awayScore);
  const cell = Math.max(SCORE.cellEm * SCORE.size, m.measureText(homeFinal).width, m.measureText(awayFinal).width);
  const separatorWidth = m.measureText(' : ').width;
  const scoreBaseline = baselineFromTop(-SCORE.size / 2, fontMetrics(m, SCORE.size), SCORE.size);
  const scoreBox = { width: 2 * SCORE.padX + 2 * cell + separatorWidth, height: SCORE.size + 2 * SCORE.padY };

  const footText = [data.shortDateLabel, data.organizerName].filter(Boolean).join(' · ').toLocaleUpperCase();
  const foot = footText
    ? measureBlock(m, footText, {
        maxWidth: FOOT.maxWidth,
        size: FOOT.size,
        min: FOOT.min,
        font: SLASH_FONTS.foot,
        spacing: FOOT.spacing,
      })
    : null;
  measurer.release();

  const scoreCenterY = SCORE.top + scoreBox.height / 2;
  const footTop = foot ? OUTPUT_HEIGHT - FOOT.bottom - foot.lineHeight : 0;

  return {
    durationSec: SLASH_INTRO_SEC,
    draw: (ctx, timeSec) => {
      const s = slashIntroState(timeSec, data.homeScore, data.awayScore);
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';

      scaleAround(ctx, CENTER_X, CENTER_Y, s.pushScale);
      ctx.translate(s.shake.dx, s.shake.dy);

      ctx.fillStyle = SLASH.bg;
      ctx.fillRect(HALF_BOX.left, HALF_BOX.top, HALF_BOX.width, HALF_BOX.height);
      drawHalf(ctx, s.splitDx, RED_HALF, SLASH.red, SLASH.redDark);
      drawHalf(ctx, -s.splitDx, DARK_HALF, SLASH.dark, SLASH.darkEnd);

      if (s.slashGrow > 0) {
        // The child grows inside the skewed bar: scaling the skewed bar itself would change its slope mid-draw.
        ctx.save();
        ctx.translate(SLASH_BAR.originX, SLASH_BAR.top);
        skewX(ctx, SLASH_BAR.skew);
        ctx.fillStyle = SLASH.white;
        ctx.fillRect(-SLASH_BAR.width / 2, 0, SLASH_BAR.width, SLASH_BAR.height * s.slashGrow);
        ctx.restore();
      }

      if (s.top.alpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.top.alpha;
        ctx.translate(0, s.top.dy);
        tracePolygon(ctx, TOP_BOX, TOP_BAND);
        ctx.fillStyle = SLASH.white;
        ctx.fill();
        ctx.restore();
      }

      if (s.champAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.champAlpha;
        ctx.fillStyle = SLASH.ink;
        fillBlock(ctx, champ, CHAMP.left, CHAMP.top, 'left');
        ctx.restore();
      }
      if (round && s.roundAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.roundAlpha;
        ctx.fillStyle = SLASH.red;
        fillBlock(ctx, round, ROUND.right, ROUND.top, 'right');
        ctx.restore();
      }

      drawLogoLayer(ctx, homeLogo, LOGO_CENTERS.home, s.homeLogo);
      drawLogoLayer(ctx, awayLogo, LOGO_CENTERS.away, s.awayLogo);

      const drawName = (block: typeof homeName, x: number, align: 'left' | 'right', slide: { dx: number; alpha: number }) => {
        if (slide.alpha <= 0) return;
        ctx.save();
        ctx.globalAlpha = slide.alpha;
        ctx.translate(slide.dx, 0);
        ctx.fillStyle = SLASH.white;
        fillBlock(ctx, block, x, NAME_TOP, align);
        ctx.restore();
      };
      drawName(homeName, TEAM.inset, 'left', s.homeName);
      drawName(awayName, OUTPUT_WIDTH - TEAM.inset, 'right', s.awayName);

      if (s.scoreScaleX > 0) {
        // `scale` and `translateX(-50%) skewX(-12deg)` share the origin of the untranslated box, so the box grows from
        // its right end — kept as approved in the mockup (set A's stage chip does the same).
        const half = scoreBox.width / 2;
        ctx.save();
        ctx.translate(CENTER_X + half, scoreCenterY);
        ctx.scale(s.scoreScaleX, 1);
        ctx.translate(-half, 0);
        skewX(ctx, SCORE.skew);
        ctx.fillStyle = SLASH.white;
        ctx.fillRect(-half, -scoreBox.height / 2, scoreBox.width, scoreBox.height);
        ctx.font = scoreFont;
        ctx.fillStyle = SLASH.ink;
        const home = s.homeShown === null ? '–' : String(s.homeShown);
        const away = s.awayShown === null ? '–' : String(s.awayShown);
        const cellLeft = -half + SCORE.padX;
        ctx.fillText(home, cellLeft + cell - ctx.measureText(home).width, scoreBaseline);
        ctx.fillText(' : ', cellLeft + cell, scoreBaseline);
        ctx.fillText(away, cellLeft + cell + separatorWidth, scoreBaseline);
        ctx.restore();
      }

      if (foot && s.foot.alpha > 0) {
        ctx.save();
        ctx.globalAlpha = s.foot.alpha;
        ctx.translate(s.foot.dx, 0);
        ctx.fillStyle = SLASH.white;
        fillBlock(ctx, foot, FOOT.left, footTop, 'left');
        ctx.restore();
      }
    },
    dispose: () => {
      homeLogo.release();
      awayLogo.release();
    },
  };
}
